import { eventChannel, EventChannel } from 'redux-saga';
import { call, put, take } from 'redux-saga/effects';

export const TAB_REFOCUSED = 'tab/refocused';

const STALE_THRESHOLD_MS = 30_000;

function createVisibilityChannel(): EventChannel<number> {
  return eventChannel<number>((emit) => {
    let hiddenAt = 0;

    const handler = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (document.visibilityState === 'visible' && hiddenAt > 0) {
        const elapsed = Date.now() - hiddenAt;
        hiddenAt = 0;
        if (elapsed >= STALE_THRESHOLD_MS) {
          emit(elapsed);
        }
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  });
}

// This saga only reports the refocus; it does NOT fan out refetches.
// TAB_REFOCUSED is one of the sync coordinator's triggers
// (infra/sync/saga.ts), which owns the decision of whether a cycle
// should run and collapses overlapping triggers with takeLatest.
//
// That collapsing is what this file used to work around by hand: a
// refocus is almost always followed within seconds by the WS layer
// reconnecting and emitting `ws/reconnected`, and when both fanned out
// independently the result was two back-to-back loading flashes. Now
// both feed one coordinator, so the second trigger cancels the first
// cycle rather than duplicating it — and the refetch still happens,
// which matters because a frozen tab can lose broadcasts without the
// socket ever reporting a close.
export function* tabFocusSaga(): Generator {
  const channel = (yield call(createVisibilityChannel)) as EventChannel<number>;

  while (true) {
    const hiddenMs = (yield take(channel)) as number;
    yield put({ type: TAB_REFOCUSED, payload: { hiddenDurationMs: hiddenMs } });
  }
}
